import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MdClose, MdSearch } from "react-icons/md";
import Loader from "src/Components/Common/Loader";

// How many chips to show before collapsing the rest behind a "+N more" chip.
const VISIBLE_CHIP_LIMIT = 6;

/**
 * Generic multi-select chip input with a searchable, portaled dropdown. Modeled
 * on RecipientsChipInput but driven by an arbitrary option list rather than the
 * recipients/users shape, so it can back the Customer / Branch / Location /
 * Technician report filters.
 *
 * label:        section heading (e.g. "Technician")
 * options:      [{ id, name, subtitle? }] selectable options
 * selectedIds:  array of currently selected option ids
 * onChange(ids): called with the next selected-id array
 * placeholder:  input placeholder when nothing is selected
 * allLabel:     text shown in the chip box when nothing is selected (e.g. "All Branches")
 * loading:      show a loading hint inside the dropdown
 * required:     show a required (*) marker next to the label
 * error:        render the chip box outline in red
 */
const MultiSelectChipInput = ({
  label,
  options = [],
  selectedIds = [],
  onChange,
  placeholder,
  allLabel,
  loading = false,
  required = false,
  error = false,
}) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const [menuRect, setMenuRect] = useState(null);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  useEffect(() => {
    if (selectedIds.length <= VISIBLE_CHIP_LIMIT) setExpanded(false);
  }, [selectedIds.length]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const updateMenuRect = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const GAP = 4;
    const MENU_MAX_HEIGHT = 270;
    const spaceBelow = window.innerHeight - rect.bottom - GAP;
    const spaceAbove = rect.top - GAP;
    const flipUp = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    const available = Math.max(120, Math.min(MENU_MAX_HEIGHT, flipUp ? spaceAbove : spaceBelow));
    const next = flipUp
      ? {
          bottom: Math.round(window.innerHeight - rect.top + GAP),
          top: null,
          left: Math.round(rect.left),
          width: Math.round(rect.width),
          maxHeight: Math.round(available),
        }
      : {
          top: Math.round(rect.bottom + GAP),
          bottom: null,
          left: Math.round(rect.left),
          width: Math.round(rect.width),
          maxHeight: Math.round(available),
        };
    setMenuRect((prev) =>
      prev &&
      prev.top === next.top &&
      prev.bottom === next.bottom &&
      prev.left === next.left &&
      prev.width === next.width &&
      prev.maxHeight === next.maxHeight
        ? prev
        : next
    );
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuRect();
    window.addEventListener("scroll", updateMenuRect, true);
    window.addEventListener("resize", updateMenuRect);
    return () => {
      window.removeEventListener("scroll", updateMenuRect, true);
      window.removeEventListener("resize", updateMenuRect);
    };
  }, [open, updateMenuRect]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedOptions = useMemo(
    () => options.filter((option) => selectedSet.has(option.id)),
    [options, selectedSet]
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter(
      (option) =>
        option.name?.toLowerCase().includes(query) ||
        option.subtitle?.toLowerCase().includes(query)
    );
  }, [options, search]);

  const allSelected = filteredOptions.length > 0 && filteredOptions.every((option) => selectedSet.has(option.id));

  const toggle = (option) => {
    if (selectedSet.has(option.id)) {
      onChange?.(selectedIds.filter((id) => id !== option.id));
    } else {
      onChange?.([...selectedIds, option.id]);
    }
  };

  const remove = (option) => {
    onChange?.(selectedIds.filter((id) => id !== option.id));
  };

  const selectAll = (checked) => {
    if (checked) {
      // Merge the currently-filtered options into the selection.
      const merged = new Set(selectedIds);
      filteredOptions.forEach((option) => merged.add(option.id));
      onChange?.(Array.from(merged));
    } else {
      const filteredIds = new Set(filteredOptions.map((option) => option.id));
      onChange?.(selectedIds.filter((id) => !filteredIds.has(id)));
    }
  };

  const hasOverflow = selectedOptions.length > VISIBLE_CHIP_LIMIT;
  const visibleOptions = hasOverflow && !expanded ? selectedOptions.slice(0, VISIBLE_CHIP_LIMIT) : selectedOptions;
  const hiddenCount = selectedOptions.length - visibleOptions.length;

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] capitalize">
          {label} {required && <span className="text-red-500">*</span>}
        </span>
        {selectedOptions.length > 0 && (
          <span className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#868DA6]">
            ({selectedOptions.length} selected)
          </span>
        )}
      </div>

      <div
        ref={anchorRef}
        className={`w-full min-h-10 bg-white rounded-[8px] outline outline-[1px] outline-offset-[-1px] p-2 flex flex-wrap content-start items-center gap-2 cursor-text ${
          error ? "outline-red-500" : "outline-[#E5E8F3]"
        }`}
        onClick={() => setOpen(true)}
      >
        {selectedOptions.length === 0 && allLabel && (
          <span className="px-1 font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]">
            {allLabel}
          </span>
        )}

        {visibleOptions.map((option) => (
          <div
            key={option.id}
            className="flex items-center gap-2 bg-[#EEF0F9] rounded-[6px] pl-3 pr-2 py-[6px] overflow-hidden"
          >
            <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] truncate">
              {option.name}
            </span>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                remove(option);
              }}
              className="text-[#7C829A] hover:text-[#1A2332] shrink-0"
            >
              <MdClose size={16} />
            </button>
          </div>
        ))}

        {hasOverflow && !expanded && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded(true);
            }}
            className="self-stretch px-3 bg-[#EEF0F9] rounded-[6px] font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#5F6477] hover:text-[#1A2332] shrink-0"
          >
            +{hiddenCount} more
          </button>
        )}

        <input
          className="flex-1 min-w-[140px] self-stretch outline-none bg-transparent font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6]"
          placeholder={selectedOptions.length === 0 && allLabel ? "" : placeholder || "Search..."}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
      </div>

      {open &&
        menuRect &&
        createPortal(
          <div
            ref={menuRef}
            data-report-dropdown="true"
            className="fixed z-[60] overflow-y-auto py-1 bg-white rounded-[8px] border border-[#C9CEE2] shadow-[0px_4px_8px_rgba(47,43,67,0.10)]"
            style={{
              top: menuRect.top ?? undefined,
              bottom: menuRect.bottom ?? undefined,
              left: menuRect.left,
              width: menuRect.width,
              maxHeight: menuRect.maxHeight,
            }}
          >
            <div className="sticky top-0 z-10 bg-white px-3 pt-1 pb-2">
              <div className="w-full h-9 bg-white rounded-[6px] border border-[#E5E8F3] px-3 flex items-center gap-2">
                <MdSearch size={18} className="text-[#868DA6] shrink-0" />
                <input
                  autoFocus
                  className="w-full outline-none bg-transparent font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6]"
                  placeholder={placeholder || "Search..."}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="text-[#868DA6] hover:text-[#1A2332] shrink-0"
                  >
                    <MdClose size={16} />
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="px-4 py-[10px]">
                <Loader variant="inline" ariaLabel="loading-options" />
              </div>
            ) : (
              <>
                {filteredOptions.length > 0 && (
                  <DropdownRow checked={allSelected} onClick={() => selectAll(!allSelected)}>
                    <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#5F6477] capitalize">
                      Select All
                    </span>
                  </DropdownRow>
                )}

                {filteredOptions.map((option) => (
                  <DropdownRow
                    key={option.id}
                    checked={selectedSet.has(option.id)}
                    onClick={() => toggle(option)}
                  >
                    <div className="flex-1 flex flex-col overflow-hidden">
                      <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#5F6477] capitalize truncate">
                        {option.name}
                      </span>
                      {option.subtitle && (
                        <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#868DA6] truncate">
                          {option.subtitle}
                        </span>
                      )}
                    </div>
                  </DropdownRow>
                ))}

                {filteredOptions.length === 0 && (
                  <div className="px-4 py-[10px] font-OpenSansRegular text-[14px] leading-[20px] text-[#868DA6]">
                    No matching results
                  </div>
                )}
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

const DropdownRow = ({ checked, onClick, children }) => (
  <div
    onClick={onClick}
    className="w-full px-4 py-[10px] flex items-center gap-2 overflow-hidden cursor-pointer hover:bg-[#EEF0F9]"
  >
    <div className="pt-[3px] pb-[3px] pr-1">
      {checked ? (
        <div className="w-[18px] h-[18px] rounded-[4px] bg-[#0065FD] flex items-center justify-center">
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
            <path
              d="M1 4.5L4 7.5L10 1.5"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      ) : (
        <div className="w-[18px] h-[18px] rounded-[4px] bg-white border border-[#C9CEE2]" />
      )}
    </div>
    {children}
  </div>
);

export default MultiSelectChipInput;
